import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Shield, CreditCard, RotateCcw } from "lucide-react";

const policies = {
  "politica-privacidade": {
    title: "Política de Privacidade",
    icon: Shield,
    lastUpdated: "05 de Junho de 2026",
    content: (
      <>
        <p className="mb-4">
          A GoProtege valoriza a sua privacidade e está comprometida com a segurança dos seus dados pessoais. 
          Esta política descreve como coletamos, usamos e protegemos as informações fornecidas por você ao utilizar a nossa loja virtual.
        </p>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">1. Coleta de Informações</h3>
        <p className="mb-4">
          Coletamos informações essenciais para o processamento de compras e entrega de produtos, tais como:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Dados Cadastrais: Nome completo, CPF ou CNPJ e e-mail.</li>
          <li>Dados de Contato: Telefone/WhatsApp.</li>
          <li>Dados de Entrega: Endereço completo (Rua, número, complemento, bairro, cidade, estado e CEP).</li>
        </ul>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">2. Uso dos Dados</h3>
        <p className="mb-4">
          Os dados coletados são utilizados exclusivamente para as seguintes finalidades:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Faturamento e emissão de notas fiscais.</li>
          <li>Logística, envio e acompanhamento de entrega dos pedidos.</li>
          <li>Suporte e atendimento ao cliente em pós-venda.</li>
          <li>Cumprimento de obrigações legais e tributárias.</li>
        </ul>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">3. Compartilhamento de Dados</h3>
        <p className="mb-4">
          Não vendemos ou alugamos seus dados pessoais. Eles são compartilhados apenas com parceiros necessários para o funcionamento dos serviços, incluindo:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Intermediadores de Pagamento (Asaas): Para processamento seguro das transações financeiras.</li>
          <li>Transportadoras e Correios: Para despacho físico das mercadorias.</li>
        </ul>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">4. Segurança da Informação</h3>
        <p className="mb-4">
          Adotamos medidas técnicas e administrativas aptas a proteger os seus dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração ou difusão, em total conformidade com a LGPD (Lei Geral de Proteção de Dados).
        </p>
      </>
    )
  },
  "politica-reembolso": {
    title: "Política de Reembolso",
    icon: CreditCard,
    lastUpdated: "05 de Junho de 2026",
    content: (
      <>
        <p className="mb-4">
          Na GoProtege, prezamos pela satisfação total dos nossos clientes. Caso precise solicitar o reembolso de uma compra realizada em nossa loja virtual, siga as regras abaixo descritas.
        </p>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">1. Elegibilidade para Reembolso</h3>
        <p className="mb-4">
          O reembolso poderá ser solicitado nas seguintes situações:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Exercício do Direito de Arrependimento de compra no prazo de 7 (sete) dias corridos.</li>
          <li>Constatação de defeito de fabricação sem possibilidade de substituição do produto por outro idêntico ou equivalente.</li>
          <li>Extravio de mercadoria confirmado pela empresa transportadora.</li>
        </ul>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">2. Condição do Produto</h3>
        <p className="mb-4">
          Para que o reembolso decorrente de devolução por arrependimento ou troca seja aprovado:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>O item deve estar em perfeito estado, sem sinais de uso ou instalação incorreta.</li>
          <li>O produto deve ser devolvido em sua embalagem original, acompanhado de todos os acessórios, cabos e manuais inclusos.</li>
        </ul>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">3. Forma e Prazo de Reembolso</h3>
        <p className="mb-4">
          O reembolso será processado no prazo de até 7 (sete) dias úteis após o recebimento e análise do produto devolvido em nossa central.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Compras via Pix: O reembolso será realizado integralmente na mesma conta bancária associada à chave Pix utilizada para a compra original.</li>
        </ul>
      </>
    )
  },
  "politica-devolucao": {
    title: "Política de Devolução",
    icon: RotateCcw,
    lastUpdated: "05 de Junho de 2026",
    content: (
      <>
        <p className="mb-4">
          Oferecemos uma política de devolução clara e justa, fundamentada no Código de Defesa do Consumidor (CDC), para garantir sua tranquilidade ao adquirir nossos rastreadores e chicotes.
        </p>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">1. Direito de Arrependimento</h3>
        <p className="mb-4">
          Conforme o artigo 49 do CDC, nas compras realizadas fora do estabelecimento físico (pela internet), o consumidor tem o prazo de **7 (sete) dias corridos**, contados a partir da data de recebimento do produto, para desistir da compra e solicitar a devolução sem necessidade de justificativa.
        </p>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">2. Procedimento de Devolução</h3>
        <p className="mb-4">
          Para iniciar uma devolução, siga as etapas abaixo:
        </p>
        <ul className="list-decimal pl-5 mb-4 space-y-1">
          <li>Entre em contato com o nosso suporte via WhatsApp no número **(15) 99192-7177** ou por e-mail no **loja@goprotege.com**.</li>
          <li>Forneça o número do seu pedido e os detalhes do item que deseja devolver.</li>
          <li>Nossa equipe fornecerá uma autorização de postagem reversa sem custo de frete para você.</li>
          <li>Embale o produto de forma segura e poste na agência dos Correios indicada.</li>
        </ul>

        <h3 className="text-lg font-bold text-[#3d3d3d] mt-6 mb-2">3. Análise do Produto Devolvido</h3>
        <p className="mb-4">
          Todos os produtos devolvidos passam por uma triagem técnica ao chegarem. A GoProtege reserva-se o direito de recusar a devolução caso o item apresente indícios de mau uso, sinais de instalação em veículos, danos físicos causados pelo cliente ou ausência de componentes essenciais.
        </p>
      </>
    )
  }
};

export default function Policies() {
  const { policySlug } = useParams();
  const policy = policies[policySlug] || policies["politica-privacidade"];
  const PolicyIcon = policy.icon;

  return (
    <div className="bg-[#FAFAFA] min-h-screen py-16 px-6 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm font-quicksand font-bold text-[#1BB0CE] hover:text-[#1BB0CE]/80 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para o Início
        </Link>

        <div className="bg-white border border-[#f1f1f3] rounded-3xl p-8 sm:p-12 shadow-sm">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#f1f1f3]">
            <div className="w-12 h-12 rounded-2xl bg-gp-gradient-light flex items-center justify-center text-[#1BB0CE]">
              <PolicyIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-quicksand font-extrabold text-[#3d3d3d] leading-none">
                {policy.title}
              </h1>
              <p className="text-xs text-[#999] mt-2 font-quicksand">
                Última atualização: {policy.lastUpdated}
              </p>
            </div>
          </div>

          <div className="text-sm text-[#555] font-quicksand leading-relaxed">
            {policy.content}
          </div>
        </div>
      </div>
    </div>
  );
}
